import { supabase } from "./supabase";
import { format } from "date-fns";

export type WhatsAppTemplate =
  | "appointment_confirm"
  | "appointment_confirm_first_time"
  | "appointment_rescheduled"
  | "appointment_cancelled_business"
  | "appointment_missed"
  | "appointment_reminder"
  | "appointment_rescheduled_client"
  | "appointment_cancelled_client"
  // HAJ-89 follow-on (deposit-rejection notification): distinct from
  // appointment_cancelled_client because the client needs to know their
  // payment proof specifically wasn't accepted, not just that the booking
  // was cancelled -- reduces to this one new template + the existing
  // appointment_confirm (already reused at approval time, HAJ-89) instead of
  // three separate deposit-phase messages.
  | "appointment_deposit_declined"
  // Unified post-visit message: dispatched 2 hours after visit completion
  // via scripts/send-review-requests.ts. Combines the post-visit thank you
  // with the tokenized private review link (/:slug/review/:id?token=...)
  // and rebooking CTA.
  | "post_visit_thank_you"
  // Sent by scripts/send-waitlist-offers.ts (service-role cron), not from the
  // browser -- not wired into getWhatsAppComponents below, that script builds
  // its own components directly (same pattern as scripts/send-reminders.ts).
  | "waitlist_slot_offered";

interface ComponentParam {
  type: "text";
  text: string;
}

interface WhatsAppComponent {
  type: "header" | "body" | "button";
  sub_type?: "url" | "quick_reply";
  index?: string;
  parameters: ComponentParam[];
}

interface ComponentOptions {
  businessName: string;
  clientName: string;
  serviceType: string;
  date?: string; // yyyy-MM-dd
  time?: string; // HH:mm or HH:mm:ss
  locationLink?: string;
  businessSlug: string;
  bookingId?: string;
}

interface SendWhatsAppParams {
  phone: string;
  templateName: WhatsAppTemplate | string;
  languageCode: "ar" | "en";
  components?: unknown[];
  businessId: string;
  /**
   * Deterministic dedupe key, forwarded to send-whatsapp-v2's idempotency
   * guard. That guard has existed since the function was written but nothing
   * on the dashboard side could reach it -- this interface had no field for
   * it -- so a double-tapped "Missed" or "Cancel" sent the client the same
   * paid template twice. Key it on the thing that must only happen once
   * (`missed_<bookingId>`), never on a timestamp or a random value.
   */
  idempotencyKey?: string;
}

/**
 * Standardizes the components for Hajez Sovereign templates.
 * Index 0: URL Button
 * Index 1: Quick Reply Button (if applicable)
 */
export const getWhatsAppComponents = (
  template: WhatsAppTemplate | string,
  options: ComponentOptions,
  language: "ar" | "en" = "en",
): WhatsAppComponent[] => {
  const {
    businessName,
    clientName,
    serviceType,
    date,
    time,
    locationLink,
    businessSlug,
    bookingId,
  } = options;

  // Format date/time if provided
  // LTR Hardening: Wrap date/time with LTR marker (\u200E) for Arabic prose to keep digits/time direction stable.
  const hardenLtr = (value: string) =>
    language === "ar" && value ? `\u200E${value}\u200E` : value;

  const formattedDate = date
    ? hardenLtr(format(new Date(date), "dd/MM/yyyy"))
    : "";
  // Handle time formatting carefully (ensure HH:mm or HH:mm:ss format)
  const timeStr = time
    ? time.split(":").length === 2
      ? `${time}:00`
      : time
    : "";
  const formattedTime = timeStr
    ? hardenLtr(format(new Date(`1970-01-01T${timeStr}`), "hh:mm a"))
    : "";

  const components: WhatsAppComponent[] = [
    {
      type: "header",
      parameters: [{ type: "text", text: businessName }],
    },
  ];

  // Body Parameters
  let bodyParams: ComponentParam[];
  switch (template) {
    case "appointment_confirm":
    case "appointment_rescheduled":
    case "appointment_rescheduled_client":
    case "appointment_reminder":
    case "appointment_cancelled_business":
      bodyParams = [
        { type: "text", text: clientName },
        { type: "text", text: serviceType },
        { type: "text", text: formattedDate },
        { type: "text", text: formattedTime },
      ];
      break;
    case "appointment_confirm_first_time":
      bodyParams = [
        { type: "text", text: clientName },
        { type: "text", text: businessName },
        { type: "text", text: serviceType },
        { type: "text", text: formattedDate },
        { type: "text", text: formattedTime },
        {
          type: "text",
          text: locationLink
            ? hardenLtr(locationLink)
            : language === "ar"
              ? "\u0631\u0627\u0628\u0637 \u0627\u0644\u0645\u0648\u0642\u0639 \u0645\u062A\u0627\u062D \u0641\u064A \u0635\u0641\u062D\u0629 \u0627\u0644\u062D\u062C\u0632"
              : "Location available on booking page",
        },
      ];
      break;
    case "appointment_missed":
    case "appointment_deposit_declined":
    case "post_visit_thank_you":
      bodyParams = [
        { type: "text", text: clientName },
        { type: "text", text: serviceType },
      ];
      break;
    case "appointment_cancelled_client":
      bodyParams = [{ type: "text", text: clientName }];
      break;
    default:
      bodyParams = [{ type: "text", text: clientName }];
  }

  components.push({
    type: "body",
    parameters: bodyParams,
  });

  // Button Parameters
  // Index 0: URL
  const isManageLink = [
    "appointment_confirm",
    "appointment_confirm_first_time",
    "appointment_rescheduled",
    "appointment_rescheduled_client",
    "appointment_reminder",
  ].includes(template);
  const urlSuffix =
    isManageLink && bookingId
      ? `${businessSlug}/booking/${bookingId}`
      : businessSlug;

  components.push({
    type: "button",
    sub_type: "url",
    index: "0",
    parameters: [{ type: "text", text: urlSuffix }],
  });

  // Index 1: Quick Reply (Confirm) - Optional based on template
  const hasQuickReply = [
    "appointment_confirm",
    "appointment_confirm_first_time",
    "appointment_rescheduled",
    "appointment_rescheduled_client",
    "appointment_reminder",
  ].includes(template);
  if (hasQuickReply) {
    components.push({
      type: "button",
      sub_type: "quick_reply",
      index: "1",
      parameters: [], // Quick replies don't take parameters usually
    });
  }

  return components;
};

/**
 * Sends a WhatsApp message via the Supabase Edge Function using Meta Cloud API.
 * This is the production-hardened version with detailed error propagation.
 */
export const sendWhatsApp = async ({
  phone,
  templateName,
  languageCode = "en",
  components = [],
  businessId,
  idempotencyKey,
}: SendWhatsAppParams): Promise<{
  success: boolean;
  error?: string;
  messageId?: string;
}> => {
  // Forensic Validation
  if (!phone || phone.length < 8) {
    return {
      success: false,
      error: "Invalid or missing recipient phone number.",
    };
  }
  if (!businessId) {
    return { success: false, error: "Missing business reference." };
  }

  // Hardcode normalization: ar/en only
  const finalLanguageCode = ["ar", "en"].includes(languageCode)
    ? languageCode
    : "en";

  try {
    const { data, error: invokeError } = await supabase.functions.invoke(
      "send-whatsapp-v2",
      {
        body: {
          business_id: businessId,
          recipient_phone: phone,
          template_name: templateName,
          language_code: finalLanguageCode,
          components: components,
          ...(idempotencyKey ? { idempotency_key: idempotencyKey } : {}),
        },
      },
    );

    // 1. Handle Edge Function Network/Invocation Errors
    if (invokeError) {
      return { success: false, error: "System failed to trigger WhatsApp." };
    }

    // 2. Handle Meta API Logical Errors Returned by the Edge Function
    if (data?.error) {
      // Extract user-friendly message if possible
      let friendlyError = "WhatsApp API rejected the message.";
      if (data.error?.message?.includes("template")) {
        friendlyError = "WhatsApp Template mismatch or not approved.";
      } else if (data.error?.message?.includes("param")) {
        friendlyError = "WhatsApp parameter mismatch for this template.";
      }

      return { success: false, error: friendlyError };
    }

    // 4. Success
    return {
      success: true,
      messageId: data?.messages?.[0]?.id || "delivered_to_provider",
    };
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    console.error("[Panic] sendWhatsApp failed:", errorMessage);
    // Critical failure - return generic error
    return {
      success: false,
      error: "Internal system error while triggering WhatsApp.",
    };
  }
};

export interface SendWhatsAppClientParams {
  bookingId: string;
  action: "cancel" | "reschedule" | "confirm";
  phone: string;
  cancellationToken?: string;
}

export const sendWhatsAppClient = async ({
  bookingId,
  action,
  phone,
  cancellationToken,
}: SendWhatsAppClientParams): Promise<{ success: boolean; error?: string }> => {
  try {
    const { data, error: invokeError } = await supabase.functions.invoke(
      "send-whatsapp-client",
      {
        body: { bookingId, action, phone, cancellationToken },
      },
    );

    if (invokeError) {
      console.error("Invocation error:", invokeError);
      return { success: false, error: "System failed to trigger WhatsApp." };
    }

    if (data?.error) {
      console.error("Function error:", data.error);
      return { success: false, error: data.error };
    }

    return { success: true };
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    console.error("sendWhatsAppClient panic:", errorMessage);
    return { success: false, error: "Internal system error." };
  }
};
